// /opt/ptg01-v1/front-extranet/components/CommandPalette.tsx
// Sprint 2026-05-17 (Sprint 3b) — Palette de commande ⌘K.
//
// UX inspirée Linear / Vercel / Notion :
//   • Modal centrée 600px desktop, drawer plein écran mobile
//   • Input focus auto, placeholder "Rechercher dans Nexvia…"
//   • Résultats groupés par catégorie (Pages / Missions / CRA / Notes de frais / Compte activité)
//   • Keyboard nav : ↑↓ navigue, ⏎ ouvre, ⎋ ferme
//   • Empty state : raccourcis vers les pages principales
//   • Debounce intégré dans useGlobalSearch (200ms)
'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import {
    Search, Briefcase, CalendarCheck, Receipt, Wallet, X, ArrowRight,
    LayoutDashboard, ArrowLeftRight, FileText, Bell, LifeBuoy, UserCircle2,
    type LucideIcon,
} from 'lucide-react';
import { useGlobalSearch, type SearchResult, type SearchResultKind } from '@/lib/useGlobalSearch';

const KIND_META: Record<SearchResultKind, { label: string; Icon: LucideIcon }> = {
    MISSION:  { label: 'Missions',         Icon: Briefcase },
    CRA:      { label: 'CRA',              Icon: CalendarCheck },
    EXPENSE:  { label: 'Notes de frais',   Icon: Receipt },
    ACTIVITY: { label: 'Compte d\'activité', Icon: Wallet },
};

// Pages statiques cherchables — toujours affichées quand pas de query, et
// matchées en local sur title si query courte.
interface PageShortcut { label: string; href: string; Icon: LucideIcon; keywords: string }
const PAGE_SHORTCUTS: PageShortcut[] = [
    { label: 'Tableau de bord',        href: '/',                      Icon: LayoutDashboard, keywords: 'dashboard accueil home' },
    { label: 'Compte d\'activité',     href: '/compte-activite',       Icon: Wallet,          keywords: 'compte solde reserve' },
    { label: 'Missions',               href: '/missions',              Icon: Briefcase,       keywords: 'mission client tjm' },
    { label: 'CRA',                    href: '/cras',                  Icon: CalendarCheck,   keywords: 'cra jours timesheet activite' },
    { label: 'Notes de frais',         href: '/depenses',              Icon: Receipt,         keywords: 'depenses frais ndf justificatif' },
    { label: 'Salaires & versements',  href: '/finance/salaires',      Icon: Wallet,          keywords: 'salaire paie bulletin' },
    { label: 'Historique virements',   href: '/historique-virements',  Icon: ArrowLeftRight,  keywords: 'virement sepa cotisation lot dsn' },
    { label: 'Mes avantages',          href: '/avantages',             Icon: Briefcase,       keywords: 'avantages cse formation' },
    { label: 'Documents',              href: '/documents',             Icon: FileText,        keywords: 'document contrat justif' },
    { label: 'Messages',               href: '/notifications',         Icon: Bell,            keywords: 'messages notification alerte' },
    { label: 'Mon profil',             href: '/mon-compte',            Icon: UserCircle2,     keywords: 'profil coordonnees securite mon compte parametres' },
    { label: 'Aide & support',         href: '/aide',                  Icon: LifeBuoy,        keywords: 'aide support faq contact' },
];

// ─── Liste plate dérivée des résultats pour gestion keyboard nav ─────────────
interface FlatRow {
    key: string;
    label: string;
    subtitle?: string;
    Icon: LucideIcon;
    href: string;
    section: string;
}

interface Props {
    open: boolean;
    onClose: () => void;
}

export default function CommandPalette({ open, onClose }: Props) {
    const router = useRouter();
    const [query, setQuery] = useState('');
    const [activeIdx, setActiveIdx] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);

    const search = useGlobalSearch(query);

    // Remise à zéro à la fermeture, pendant le rendu : la palette ne se rouvre
    // jamais, même un rendu, sur la recherche précédente.
    const [ouvertureVue, setOuvertureVue] = useState(open);
    if (ouvertureVue !== open) {
        setOuvertureVue(open);
        if (!open) {
            setQuery('');
            setActiveIdx(0);
        }
    }

    // Focus du champ à l'ouverture (effet de bord sur le DOM, pas de state).
    useEffect(() => {
        if (!open) return;
        const t = setTimeout(() => inputRef.current?.focus(), 30);
        return () => clearTimeout(t);
    }, [open]);

    // ─── Construction de la liste plate ───────────────────────────────────────
    const flatRows = useMemo<FlatRow[]>(() => {
        const rows: FlatRow[] = [];

        // Pages : toujours visibles si pas de query (raccourcis) — sinon filtrées localement
        const q = query.trim().toLowerCase();
        const pagesFiltered = q
            ? PAGE_SHORTCUTS.filter(p =>
                p.label.toLowerCase().includes(q) || p.keywords.includes(q),
            )
            : PAGE_SHORTCUTS;
        for (const p of pagesFiltered.slice(0, q ? 5 : PAGE_SHORTCUTS.length)) {
            rows.push({
                key: `page:${p.href}`,
                label: p.label,
                Icon: p.Icon,
                href: p.href,
                section: q ? 'Pages' : 'Aller à',
            });
        }

        // Résultats API (seulement si la query est ≥ 2 chars)
        if (search.data) {
            const pushKind = (kind: SearchResultKind, items: SearchResult[]) => {
                if (!items.length) return;
                const { label, Icon } = KIND_META[kind];
                for (const r of items) {
                    rows.push({
                        key: `${kind}:${r.id}`,
                        label: r.title,
                        subtitle: r.subtitle,
                        Icon,
                        href: r.href,
                        section: label,
                    });
                }
            };
            pushKind('MISSION',  search.data.missions);
            pushKind('CRA',      search.data.cras);
            pushKind('EXPENSE',  search.data.expenses);
            pushKind('ACTIVITY', search.data.activity);
        }

        return rows;
    }, [query, search.data]);

    // Index de la ligne active borné à la liste courante : dérivé plutôt que
    // corrigé après coup par un effet, qui laissait passer un rendu hors bornes.
    const activeIndex = Math.min(activeIdx, Math.max(0, flatRows.length - 1));

    // ─── Keyboard navigation ──────────────────────────────────────────────────
    const onKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIdx(i => Math.min(i + 1, flatRows.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIdx(i => Math.max(i - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const row = flatRows[activeIndex];
            if (row) navigate(row.href);
        }
    };

    const navigate = (href: string) => {
        onClose();
        router.push(href);
    };

    // Scroll l'item actif dans la viewport (smooth)
    useEffect(() => {
        const el = listRef.current?.querySelector(`[data-rowidx="${activeIndex}"]`);
        if (el) el.scrollIntoView({ block: 'nearest', behavior: 'auto' });
    }, [activeIndex]);

    if (!open) return null;

    // ─── Render ───────────────────────────────────────────────────────────────
    let lastSection = '';

    return (
        <div
            data-testid="command-palette"
            className="fixed inset-0 z-[200] flex items-start justify-center px-4 lg:pt-[18vh] pt-0"
            onClick={onClose}
        >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" />

            {/* Modal — plein écran < lg, centré ≥ lg */}
            <div
                onClick={e => e.stopPropagation()}
                onKeyDown={onKeyDown}
                className="relative w-full lg:max-w-[600px] bg-white lg:rounded-2xl shadow-2xl border-2 border-[#000091]/15 flex flex-col h-full lg:h-auto lg:max-h-[70vh] overflow-hidden"
            >
                {/* Input row */}
                <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200 shrink-0">
                    <Search className="w-4 h-4 text-slate-400 shrink-0" />
                    <input
                        ref={inputRef}
                        type="text"
                        value={query}
                        onChange={e => { setQuery(e.target.value); setActiveIdx(0); }}
                        placeholder="Rechercher dans Nexvia… (missions, CRA, frais)"
                        data-testid="command-palette-input"
                        className="flex-1 outline-none bg-transparent text-[14px] text-slate-900 placeholder-slate-400"
                    />
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Fermer la recherche"
                        className="text-slate-400 hover:text-slate-600"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Footer mobile (avec indication clavier sur desktop) */}
                <div className="hidden lg:flex items-center gap-3 px-4 py-1.5 bg-slate-50 border-b border-slate-200 text-[10px] text-slate-500 shrink-0">
                    <Kbd>↑↓</Kbd><span>naviguer</span>
                    <Kbd>↵</Kbd><span>ouvrir</span>
                    <Kbd>esc</Kbd><span>fermer</span>
                </div>

                {/* Results */}
                <div ref={listRef} className="flex-1 overflow-y-auto">
                    {/* Loading */}
                    {search.isFetching && query.trim().length >= 2 && (
                        <div className="px-4 py-3 text-[11px] text-slate-400">Recherche en cours…</div>
                    )}

                    {/* Error */}
                    {search.isError && (
                        <div className="px-4 py-6 text-center">
                            <div className="text-[12px] font-semibold text-rose-700">Recherche indisponible</div>
                            <div className="text-[11px] text-slate-500 mt-1">
                                {(search.error as Error)?.message || 'Réessaye dans un instant.'}
                            </div>
                        </div>
                    )}

                    {/* Empty (avec query mais 0 résultats) */}
                    {!search.isFetching && query.trim().length >= 2 && flatRows.length === 0 && (
                        <div className="px-4 py-8 text-center">
                            <Search className="w-6 h-6 text-slate-300 mx-auto mb-2" />
                            <div className="text-[12px] font-semibold text-slate-700">Aucun résultat pour « {query} »</div>
                            <div className="text-[11px] text-slate-500 mt-1">
                                Essaie un autre mot-clé (mission, mois, montant…).
                            </div>
                        </div>
                    )}

                    {/* Rows */}
                    {flatRows.map((row, i) => {
                        const sectionHeader = row.section !== lastSection ? (
                            <div className="px-4 pt-3 pb-1 text-[9.5px] font-bold uppercase tracking-wider text-slate-400">
                                {row.section}
                            </div>
                        ) : null;
                        lastSection = row.section;
                        const isActive = i === activeIndex;
                        return (
                            <React.Fragment key={row.key}>
                                {sectionHeader}
                                <button
                                    type="button"
                                    data-rowidx={i}
                                    onMouseEnter={() => setActiveIdx(i)}
                                    onClick={() => navigate(row.href)}
                                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                                        isActive ? 'bg-[#000091]/[0.05]' : 'hover:bg-slate-50'
                                    }`}
                                >
                                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${
                                        isActive ? 'bg-[#000091]/10 text-[#000091]' : 'bg-slate-100 text-slate-600'
                                    }`}>
                                        <row.Icon className="w-3.5 h-3.5" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[12.5px] font-semibold text-slate-900 truncate">{row.label}</div>
                                        {row.subtitle && (
                                            <div className="text-[10.5px] text-slate-500 truncate">{row.subtitle}</div>
                                        )}
                                    </div>
                                    {isActive && <ArrowRight className="w-3.5 h-3.5 text-[#000091] shrink-0" />}
                                </button>
                            </React.Fragment>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

function Kbd({ children }: { children: React.ReactNode }) {
    return (
        <kbd className="inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded bg-white border border-slate-300 text-[9px] font-bold text-slate-700">
            {children}
        </kbd>
    );
}
